// Words into maps on the cortex (ba-web, on ba-mind's MIND-003C model, sims/mind/neuroquery-cortex-v1): the typed
// topic's literature map on the HD cortex the viewport draws, through its live vertex layer in fsaverage5 space,
// beside the slices of the native model (features/text.ts). ba-mind's adapter (pipelines/mind/text/cortex) loads
// and hash-checks the model once, binds the layer (`surface: 'cortex'`, `space: 'fsaverage5'`, 20,484 values, left
// then right) and stages each topic's map; unknown text clears it, and "not pain" maps as "pain". The projection
// passed a conditional fidelity test only (18,474 supported vertices; the whole-head test MIND-003B failed), which
// the Methods keep, and the view claims no biological accuracy. A viewport without live layers (the 2D preview,
// the mock) is said so, and the model isn't fetched for it.

import { useEffect, useRef, useState } from 'react';
import { create } from 'zustand';
import { loadCortexEncoder, type CortexEncoder } from '../../../../pipelines/mind/text/cortex/encoder.mjs';
import { createCortexTextLayer, type CortexTextViewport } from '../../../../pipelines/mind/text/cortex/layer.mjs';
import { defaultSource } from '../data/sims/files.ts';
import { useAtlas } from '../app/store.ts';
import { getViewport } from '../viewport/host.ts';
import type { LiveViewport } from './stim.ts';
import { SCALE, useText } from './text.ts';

export const CORTEX_RUN = 'mind/neuroquery-cortex-v1';
export const CORTEX_LAYER = 'mind-text';
/** The model directory's URL, under the app's sims root. */
export const cortexUrl = (): string => `${defaultSource().base}${CORTEX_RUN}/`;
/** The lead's look for a signed field on the cortex (#705, #711), on the slices' own ±8 scale, so the cortex and the
 *  slices read on one scale: slate near zero, bare tissue where the model has no vertex (NaN). */
export const CORTEX_LOOK = { colormap: 'change', range: [-SCALE, SCALE] as [number, number], centre: 0, albedoFloor: 0.4, blend: 'mix', albedo: 0.7, emission: 0 };
/** The model's own numbers (its header and ba-mind's MIND-003C note), for the panel and Methods. */
export const CORTEX_FACTS = { vertices: 20_484, supported: 18_474, rmse: '9.9 × 10⁻⁸', zero: '2.32', queries: 32, bytes: '20.5 MB' } as const;

export type CortexStatus = 'idle' | 'loading' | 'ready' | 'missing' | 'error';
export type CortexLayerState = 'off' | 'pushed' | 'none';
export interface CortexCoverage {
  /** The layer's own vertices (20,484) and, where the viewport says, the displayed vertices it covers. */
  vertices: number;
  covered: number | null;
  of: number | null;
}

export interface TextCortexState {
  status: CortexStatus;
  error: string | null;
  model: CortexEncoder | null;
  /** Whether topics reach the viewport's layer; `none` on a viewport without live layers. */
  layer: CortexLayerState;
  covered: CortexCoverage | null;
  /** What the layer last showed: a map, or cleared for text the model doesn't read. */
  shown: 'map' | 'cleared' | null;
  /** Start loading the model, once. */
  open(): void;
  /** The model, loaded or given (tests). */
  attach(model: CortexEncoder): void;
  setLayer(layer: CortexLayerState, covered?: CortexCoverage | null): void;
  setShown(shown: 'map' | 'cleared' | null): void;
}

let loading: Promise<void> | null = null;

export const useTextCortex = create<TextCortexState>((set, get) => ({
  status: 'idle',
  error: null,
  model: null,
  layer: 'off',
  covered: null,
  shown: null,
  open() {
    if (get().model || loading) return;
    set({ status: 'loading', error: null });
    loading = loadCortexEncoder(cortexUrl())
      .then((m) => get().attach(m))
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        loading = null;
        set({ status: /^Model fetch failed: header\.json/.test(message) ? 'missing' : 'error', error: message });
      });
  },
  attach(model) {
    set({ model, status: 'ready', error: null });
  },
  setLayer: (layer, covered = null) => set({ layer, covered }),
  setShown: (shown) => set({ shown }),
}));

/** Forget the model and the layer's state (tests). */
export function resetTextCortex(): void {
  loading = null;
  useTextCortex.setState({ status: 'idle', error: null, model: null, layer: 'off', covered: null, shown: null });
}

/** The supported vertices the model's own mask counts, else the test's number. */
export const supportedOf = (model: Pick<CortexEncoder, 'valid'> | null): number => (model ? model.valid.reduce((a, v) => a + v, 0) : CORTEX_FACTS.supported);

/** The panel's line on the cortex: what's drawn there, or why nothing is. */
export function cortexLine(s: Pick<TextCortexState, 'status' | 'error' | 'layer' | 'covered' | 'model'>): string {
  if (s.layer === 'none') return 'On the cortex: not drawn by this viewport; the slices alone.';
  if (s.status === 'missing') return 'On the cortex: the cortex model isn’t on this server; the slices alone.';
  if (s.status === 'error') return `On the cortex: the cortex model didn’t load (${s.error ?? 'unknown error'}); the slices alone.`;
  if (s.status !== 'ready') return `On the cortex: loading the cortex model (${CORTEX_FACTS.bytes})…`;
  if (s.layer !== 'pushed') return 'On the cortex: waiting for the viewport…';
  const of = s.covered?.of;
  return `On the cortex: ${CORTEX_FACTS.vertices.toLocaleString()} fsaverage5 values${of ? ` on ${of.toLocaleString()} displayed vertices` : ''}; ${supportedOf(s.model).toLocaleString()} supported, the rest bare.`;
}

type CortexLayer = Awaited<ReturnType<typeof createCortexTextLayer>>;

/** The topic on the cortex while the view is on: the model opened when the viewport has live layers, the layer bound
 *  once the pack is in (a load removes every layer, so it's bound again after one), each topic shown as it changes,
 *  and the layer disposed when the view closes. */
export function useTextCortexLayer(active: boolean): void {
  const loadStatus = useAtlas((s) => s.load.status);
  const query = useText((s) => s.query);
  const nativeStatus = useText((s) => s.status);
  const model = useTextCortex((s) => s.model);
  const open = useTextCortex((s) => s.open);
  const setLayer = useTextCortex((s) => s.setLayer);
  const layerRef = useRef<CortexLayer | null>(null);
  const [made, setMade] = useState(0);
  useEffect(() => {
    // Once the native model (the slices) has resolved, so the two downloads don't compete on a slow link (lead, 05:39 UTC).
    if (!active || !['ready', 'missing', 'error'].includes(nativeStatus)) return;
    let live = true;
    void Promise.resolve(getViewport()).then((vp) => {
      if (!live) return;
      if (!vp || typeof (vp as LiveViewport).setVertexData !== 'function') {
        setLayer('none');
        return;
      }
      open();
    });
    return () => {
      live = false;
    };
  }, [active, nativeStatus, open, setLayer]);
  useEffect(() => {
    if (!active || !model || loadStatus !== 'ready') return;
    let live = true;
    void (async () => {
      const vp = (await getViewport()) as LiveViewport | null;
      if (!vp || !live || typeof vp.setVertexData !== 'function') return;
      try {
        const layer = await createCortexTextLayer(vp as unknown as CortexTextViewport, model, { id: CORTEX_LAYER, look: { ...CORTEX_LOOK } });
        if (!live) {
          void layer.dispose();
          return;
        }
        layerRef.current = layer;
        const b = layer.binding as { vertices?: number; covered?: number; of?: number };
        setLayer('pushed', { vertices: b.vertices ?? 0, covered: typeof b.covered === 'number' ? b.covered : null, of: typeof b.of === 'number' ? b.of : null });
        setMade((n) => n + 1);
      } catch (err) {
        console.warn('[text] cortex layer', err);
        if (live) setLayer('none');
      }
    })();
    return () => {
      live = false;
      const layer = layerRef.current;
      layerRef.current = null;
      if (layer) void Promise.resolve(layer.dispose()).catch(() => undefined);
    };
  }, [active, model, loadStatus, setLayer]);
  useEffect(() => {
    const layer = layerRef.current;
    if (!made || !layer) return;
    try {
      const r = layer.show(query);
      useTextCortex.getState().setShown(r.status === 'encoded' ? 'map' : 'cleared');
    } catch (err) {
      console.warn('[text] cortex show', err);
    }
  }, [query, made]);
  useEffect(() => {
    if (!active) return;
    return () => {
      setMade(0);
      useTextCortex.getState().setLayer('off');
      useTextCortex.getState().setShown(null);
    };
  }, [active]);
}

// The checks read the cortex's state here, as they read the slices' at window.__text.
if (typeof window !== 'undefined') (window as unknown as { __textCortex?: typeof useTextCortex }).__textCortex = useTextCortex;
